import { ChangeDetectionStrategy, Component, input, TemplateRef } from '@angular/core';
import { HlmIconComponent, HlmTooltipDirective } from '../../ui';

/**
 * Help icon that explains a label on hover or keyboard focus (Style Guide §7.3): a real `<button>`
 * carries the accessible name and the tooltip, the `lucideCircleHelp` icon is decorative, and
 * `after:-inset-3.5` widens the hit area to 44×44 px without moving the layout.
 *
 * Place it as a sibling of the label it explains, never inside a `<label>`.
 *
 * @ticket PS-6598
 */
@Component({
  selector: 'cmt-help-icon',
  imports: [HlmIconComponent, HlmTooltipDirective],
  template: `
    <button
      type="button"
      class="relative inline-flex cursor-help items-center rounded-sm after:absolute after:-inset-3.5 text-muted-foreground transition-colors hover:text-foreground focus-visible:text-foreground focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:[outline-color:var(--ring)]"
      [attr.aria-label]="'Help: ' + label()"
      [hlmTooltip]="text()">
      <hlm-icon name="lucideCircleHelp" size="1rem" aria-hidden="true" />
    </button>
  `,
  host: { class: 'inline-flex' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HelpIconComponent {
  /** What the icon explains; becomes the accessible name "Help: {label}". */
  readonly label = input.required<string>();
  /** Tooltip content: plain text, or a template for structured content. */
  readonly text = input.required<string | TemplateRef<void>>();
}
